import React, { useState, useEffect } from "react";
import Section from "./layouts/Section";
import { userSelect, allRemoteSettingsSelect } from '../redux/selectors/index';
import { useSelector, useDispatch } from 'react-redux';
import { WebLink, SiteWide } from "./customComponents";
import { 
    AiOutlineSearch, 
    AiOutlineDown, 
    AiOutlineUp,
    AiOutlineQuestionCircle,
    AiOutlineBulb,
    AiOutlineInfoCircle,
    AiOutlineSetting,
    AiOutlineLock,
    AiOutlineWallet
} from "react-icons/ai";

function Faq() {
    const site_settings = useSelector(allRemoteSettingsSelect);
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedCategory, setSelectedCategory] = useState('all');
    const [expandedItems, setExpandedItems] = useState(new Set());

    // FAQ categories based on content analysis
    const categories = [
        { id: 'all', name: 'All Questions', icon: AiOutlineQuestionCircle },
        { id: 'general', name: 'General', icon: AiOutlineInfoCircle },
        { id: 'security', name: 'Security', icon: AiOutlineLock },
        { id: 'wallet', name: 'Wallet', icon: AiOutlineWallet },
        { id: 'trading', name: 'Trading', icon: AiOutlineBulb },
        { id: 'settings', name: 'Settings', icon: AiOutlineSetting }
    ];

    // Categorize FAQ items based on keywords in questions
    const categorizeFAQ = (question) => {
        const q = question.toLowerCase();
        if (q.includes('wallet') || q.includes('deposit') || q.includes('withdraw')) return 'wallet';
        if (q.includes('security') || q.includes('password') || q.includes('safe')) return 'security';
        if (q.includes('trade') || q.includes('investment') || q.includes('profit')) return 'trading';
        if (q.includes('setting') || q.includes('profile') || q.includes('account')) return 'settings';
        return 'general';
    };

    // Filter FAQ items based on search and category
    const filteredFAQs = site_settings?.faq?.filter(item => {
        const matchesSearch = item.question.toLowerCase().includes(searchTerm.toLowerCase()) ||
                            item.answer.toLowerCase().includes(searchTerm.toLowerCase());
        const matchesCategory = selectedCategory === 'all' || categorizeFAQ(item.question) === selectedCategory;
        return matchesSearch && matchesCategory;
    }) || [];

    const toggleExpanded = (index) => {
        const newExpanded = new Set(expandedItems);
        if (newExpanded.has(index)) {
            newExpanded.delete(index);
        } else {
            newExpanded.add(index);
        }
        setExpandedItems(newExpanded);
    };

    const getCategoryIcon = (categoryId) => {
        const category = categories.find(cat => cat.id === categoryId);
        return category ? category.icon : AiOutlineQuestionCircle;
    };

    return (
        <SiteWide id="main" title="FAQ">
            <Section title="FAQ">
                <div className="min-h-screen p-4">
                    <div className="max-w-6xl mx-auto pt-8">
                        {/* Modern Header */}
                        <div className="text-center mb-12">
                            <h1 className="text-5xl font-bold text-white mb-4 bg-gradient-to-r from-white via-blue-200 to-purple-200 bg-clip-text text-transparent">
                                Frequently Asked Questions
                            </h1>
                            <p className="text-xl text-blue-200 max-w-2xl mx-auto">
                                Find answers to common questions about our platform, services, and features
                            </p>
                        </div>

                        {/* Search and Filter Section */}
                        <div className="mb-8">
                            <div className="flex flex-col lg:flex-row gap-6">
                                {/* Search Bar */}
                                <div className="flex-1">
                                    <div className="relative group">
                                        <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                                            <AiOutlineSearch className="text-gray-400 group-focus-within:text-blue-400 transition-colors" size={20} />
                                        </div>
                                        <input
                                            type="text"
                                            placeholder="Search questions or answers..."
                                            value={searchTerm}
                                            onChange={(e) => setSearchTerm(e.target.value)}
                                            className="w-full pl-12 pr-4 py-4 bg-white/10 backdrop-blur-xl border border-white/20 rounded-2xl text-white placeholder-gray-400 focus:outline-none focus:border-blue-400 focus:bg-white/15 transition-all duration-300"
                                        />
                                        <div className="absolute inset-0 bg-gradient-to-r from-blue-500/5 to-purple-500/5 rounded-2xl opacity-0 group-focus-within:opacity-100 transition-opacity duration-300"></div>
                                    </div>
                                </div>

                                {/* Category Filter */}
                                <div className="lg:w-80">
                                    <select
                                        value={selectedCategory}
                                        onChange={(e) => setSelectedCategory(e.target.value)}
                                        className="w-full px-4 py-4 bg-white/10 backdrop-blur-xl border border-white/20 rounded-2xl text-white focus:outline-none focus:border-blue-400 transition-all duration-300 appearance-none cursor-pointer"
                                    >
                                        {categories.map(category => {
                                            const Icon = category.icon;
                                            return (
                                                <option key={category.id} value={category.id} className="bg-gray-800 text-white">
                                                    {category.name}
                                                </option>
                                            );
                                        })}
                                    </select>
                                </div>
                            </div>

                            {/* Category Pills */}
                            <div className="flex flex-wrap gap-3 mt-6">
                                {categories.map(category => {
                                    const Icon = category.icon;
                                    const isActive = selectedCategory === category.id;
                                    return (
                                        <button
                                            key={category.id}
                                            onClick={() => setSelectedCategory(category.id)}
                                            className={`flex items-center gap-2 px-4 py-2 rounded-xl transition-all duration-300 ${
                                                isActive 
                                                    ? 'bg-gradient-to-r from-blue-500 to-purple-600 text-white shadow-lg shadow-blue-500/25' 
                                                    : 'bg-white/10 text-gray-300 hover:bg-white/20 hover:text-white'
                                            }`}
                                        >
                                            <Icon size={16} />
                                            <span className="text-sm font-medium">{category.name}</span>
                                        </button>
                                    );
                                })}
                            </div>
                        </div>

                        {/* FAQ Results Count */}
                        <div className="mb-6">
                            <p className="text-gray-300 text-sm">
                                {filteredFAQs.length} question{filteredFAQs.length !== 1 ? 's' : ''} found
                                {searchTerm && ` for "${searchTerm}"`}
                                {selectedCategory !== 'all' && ` in ${categories.find(cat => cat.id === selectedCategory)?.name}`}
                            </p>
                        </div>

                        {/* FAQ Items */}
                        <div className="space-y-4">
                            {filteredFAQs.length > 0 ? (
                                filteredFAQs.map((item, index) => {
                                    const isExpanded = expandedItems.has(index);
                                    const categoryId = categorizeFAQ(item.question);
                                    const CategoryIcon = getCategoryIcon(categoryId);
                                    
                                    return (
                                        <div
                                            key={index}
                                            className="group relative backdrop-blur-xl bg-white/10 rounded-2xl border border-white/20 shadow-2xl overflow-hidden hover:bg-white/15 transition-all duration-300"
                                        >
                                            {/* Decorative gradient overlay */}
                                            <div className="absolute inset-0 bg-gradient-to-r from-blue-500/5 to-purple-500/5 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                                            
                                            <div className="relative">
                                                {/* Question Header */}
                                                <button
                                                    onClick={() => toggleExpanded(index)}
                                                    className="w-full p-6 text-left flex items-center justify-between gap-4 hover:bg-white/5 transition-colors duration-200"
                                                >
                                                    <div className="flex items-start gap-4 flex-1">
                                                        {/* Category Icon */}
                                                        <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-purple-600 rounded-xl flex items-center justify-center shadow-lg flex-shrink-0">
                                                            <CategoryIcon className="text-white" size={20} />
                                                        </div>
                                                        
                                                        {/* Question Text */}
                                                        <div className="flex-1">
                                                            <h3 className="text-lg font-semibold text-white mb-1 group-hover:text-blue-200 transition-colors">
                                                                {item.question}
                                                            </h3>
                                                            <div className="flex items-center gap-2">
                                                                <span className={`px-2 py-1 rounded-lg text-xs font-medium ${
                                                                    categoryId === 'wallet' ? 'bg-green-500/20 text-green-400' :
                                                                    categoryId === 'security' ? 'bg-red-500/20 text-red-400' :
                                                                    categoryId === 'trading' ? 'bg-yellow-500/20 text-yellow-400' :
                                                                    categoryId === 'settings' ? 'bg-purple-500/20 text-purple-400' :
                                                                    'bg-blue-500/20 text-blue-400'
                                                                }`}>
                                                                    {categories.find(cat => cat.id === categoryId)?.name}
                                                                </span>
                                                            </div>
                                                        </div>
                                                    </div>
                                                    
                                                    {/* Expand/Collapse Icon */}
                                                    <div className="w-8 h-8 bg-white/10 rounded-lg flex items-center justify-center flex-shrink-0">
                                                        {isExpanded ? (
                                                            <AiOutlineUp className="text-white" size={16} />
                                                        ) : (
                                                            <AiOutlineDown className="text-white" size={16} />
                                                        )}
                                                    </div>
                                                </button>

                                                {/* Answer Content */}
                                                {isExpanded && (
                                                    <div className="px-6 pb-6 border-t border-white/10">
                                                        <div className="pt-4">
                                                            <div className="prose prose-invert max-w-none">
                                                                <p className="text-gray-300 leading-relaxed whitespace-pre-wrap">
                                                                    {item.answer}
                                                                </p>
                                                            </div>
                                                        </div>
                                                    </div>
                                                )}
                                            </div>

                                            {/* Progress indicator */}
                                            <div className="absolute bottom-0 left-0 w-full h-1 bg-gradient-to-r from-blue-500 to-purple-500 opacity-0 group-hover:opacity-60 transition-opacity duration-300"></div>
                                        </div>
                                    );
                                })
                            ) : (
                                /* No Results Found */
                                <div className="text-center py-16">
                                    <div className="w-24 h-24 bg-white/10 rounded-full flex items-center justify-center mx-auto mb-6">
                                        <AiOutlineSearch className="text-gray-400" size={32} />
                                    </div>
                                    <h3 className="text-xl font-semibold text-white mb-2">No questions found</h3>
                                    <p className="text-gray-400 mb-6">
                                        {searchTerm 
                                            ? `No questions match your search "${searchTerm}"`
                                            : 'No questions available in this category'
                                        }
                                    </p>
                                    <button
                                        onClick={() => {
                                            setSearchTerm('');
                                            setSelectedCategory('all');
                                        }}
                                        className="px-6 py-3 bg-gradient-to-r from-blue-500 to-purple-600 rounded-xl text-white font-medium hover:from-blue-600 hover:to-purple-700 transition-all duration-300 hover:scale-105 shadow-lg hover:shadow-blue-500/25"
                                    >
                                        Clear Filters
                                    </button>
                                </div>
                            )}
                        </div>

                        {/* Help Section */}
                        {filteredFAQs.length > 0 && (
                            <div className="mt-12 text-center">
                                <div className="bg-white/5 rounded-2xl p-8 border border-white/10 backdrop-blur-xl">
                                    <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-purple-600 rounded-2xl flex items-center justify-center mx-auto mb-4">
                                        <AiOutlineBulb className="text-white" size={24} />
                                    </div>
                                    <h3 className="text-xl font-bold text-white mb-2">Still need help?</h3>
                                    <p className="text-gray-300 mb-6">
                                        Can't find what you're looking for? Our support team is here to help you.
                                    </p>
                                    <div className="flex flex-col sm:flex-row gap-4 justify-center">
                                        {site_settings?.site_settings?.telegram != '' && (
                                            <button 
                                                onClick={() => window.open(site_settings?.site_settings?.telegram_link)}
                                                className="px-6 py-3 bg-gradient-to-r from-green-500 to-blue-600 rounded-xl text-white font-medium hover:from-green-600 hover:to-blue-700 transition-all duration-300 hover:scale-105 shadow-lg hover:shadow-green-500/25"
                                            >
                                                Telegram
                                            </button>
                                        )}
                                        {site_settings?.site_settings?.whatsapp != '' && (
                                            <button 
                                                onClick={() => window.open(site_settings?.site_settings?.whatsapp)}
                                                className="px-6 py-3 bg-white/10 border border-white/20 rounded-xl text-white font-medium hover:bg-white/20 transition-all duration-300"
                                            >
                                                Customer Care
                                            </button>
                                        )}
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </Section>
        </SiteWide>
    );
}

export default Faq;
